"use client";

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

export type ToastSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';

export interface ThreatToast {
  id: string;
  severity: ToastSeverity;
  threat_type: string;
  target: string;
  source_ip: string;
  technique: string;
  occurred_at: string;
}

interface ThreatToastContextValue {
  toasts: ThreatToast[];
  unreadCount: number;
  addToast: (toast: Omit<ThreatToast, 'id'>) => void;
  dismissToast: (id: string) => void;
  dismissAll: () => void;
  clearUnread: () => void;
}

const ThreatToastContext = createContext<ThreatToastContextValue | null>(null);

export function ThreatToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ThreatToast[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const counterRef = useRef(0);

  const addToast = useCallback((toast: Omit<ThreatToast, 'id'>) => {
    counterRef.current += 1;
    const id = `toast-${Date.now()}-${counterRef.current}`;
    const newToast: ThreatToast = { ...toast, id };

    setToasts((prev) => {
      // Keep max 5 toasts visible at once
      const updated = [newToast, ...prev].slice(0, 5);
      return updated;
    });
    setUnreadCount((prev) => prev + 1);

    // Auto-dismiss after 8s for medium/low/info, 12s for high, never for critical
    if (toast.severity === 'medium' || toast.severity === 'low' || toast.severity === 'info') {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 8000);
    } else if (toast.severity === 'high') {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 12000);
    }
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const clearUnread = useCallback(() => {
    setUnreadCount(0);
  }, []);

  return (
    <ThreatToastContext.Provider value={{ toasts, unreadCount, addToast, dismissToast, dismissAll, clearUnread }}>
      {children}
    </ThreatToastContext.Provider>
  );
}

export function useThreatToast() {
  const ctx = useContext(ThreatToastContext);
  if (!ctx) throw new Error('useThreatToast must be used within ThreatToastProvider');
  return ctx;
}
